import { OpenPanel } from '@openpanel/web'
import type { Router } from 'vue-router'
import type {
  AnalyticsConfig,
  AnalyticsEventName,
  AnalyticsEventProperties,
  AnalyticsUser,
} from '@/types/analytics'

let openpanel: OpenPanel | null = null
let isInitialized = false

/**
 * Initialize OpenPanel analytics for the Vue application.
 *
 * @param router - The Vue Router instance for automatic page view tracking
 * @param config - Analytics configuration options
 */
export function initAnalytics(router: Router, config: AnalyticsConfig): void {
  // Skip initialization if disabled or no client ID
  if (!config.enabled || !config.clientId) {
    console.warn('Analytics not configured, tracking disabled')
    return
  }

  try {
    openpanel = new OpenPanel({
      clientId: config.clientId,
      apiUrl: config.apiUrl,
    })
    isInitialized = true

    // Set up automatic page view tracking via router afterEach hook
    router.afterEach((to) => {
      if (isInitialized && openpanel) {
        openpanel.track('screen_view', {
          name: to.name?.toString() ?? to.path,
          path: to.path,
        })
      }
    })
  } catch (error) {
    console.error('Failed to initialize analytics:', error)
  }
}

/**
 * Identify the current user for analytics tracking.
 *
 * @param user - The user data to set for identification
 */
export function identifyUser(user: AnalyticsUser): void {
  if (!isInitialized || !openpanel) {
    return
  }

  try {
    openpanel.identify({
      profileId: user.id,
      email: user.email,
      firstName: user.name,
    })
  } catch (error) {
    console.error('Failed to identify user for analytics:', error)
  }
}

/**
 * Track a custom analytics event.
 *
 * @param name - The event name
 * @param properties - Optional event properties
 */
export function trackEvent(
  name: AnalyticsEventName,
  properties?: AnalyticsEventProperties
): void {
  if (!isInitialized || !openpanel) {
    return
  }

  try {
    openpanel.track(name, properties)
  } catch (error) {
    console.error('Failed to track analytics event:', error)
  }
}

/**
 * Reset user identity (call on logout).
 */
export function resetUser(): void {
  if (!isInitialized || !openpanel) {
    return
  }

  try {
    openpanel.clear()
  } catch (error) {
    console.error('Failed to reset analytics user:', error)
  }
}

/**
 * Check if analytics is initialized.
 * Useful for testing and debugging.
 */
export function isAnalyticsInitialized(): boolean {
  return isInitialized
}
